
import { ArrowUp, ArrowDown, Award } from 'lucide-react';
import { useAuth } from 'providers/AuthProvider'
import { useState } from 'react';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { Answer, Question } from './DataDefinitions';
import { useTranslation } from 'react-i18next';
import CommentForm from './CommentForm';
interface QuestionDetailProps {
    question: Question;
    answers: Answer[];
    onSubmit: () => void;
    onError?: (error: any) => void;
}

const QuestionDetail: React.FC<QuestionDetailProps> = ({ question, answers, onSubmit, onError }) => {

    const auth = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        question: question,
        content: '',
    });

    const handleSubmitAnswer = async (e: React.FormEvent) => {
        e.preventDefault();

        const form = new FormData();
        form.append('answerForm', JSON.stringify(formData));

        try {
            const response = await apiClient.post(
                '/v1/forum/answer', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            onSubmit();
        } catch (error) {
            console.error('Request failed:', error);
            if (onError) onError(error);
        }

    };

    const handleCommentSubmit = async (comment: { content: string; parentId: number; parentType: string }) => {

        const form = new FormData();
        form.append('commentForm', JSON.stringify(comment));

        try {
            await apiClient.post(
                '/v1/forum/comment', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            onSubmit();
        } catch (error) {
            console.error('Request failed:', error);
            if (onError) onError(error);
        }
    };



    return (
        <>

            {/* Question Header */}
            <div className="mb-4">
                <h1 className="h2 mb-4">{question.title}</h1>
                <div className="d-flex gap-3 text-secondary  mb-4">
                    <span>Asked: <strong>{question.inserted_at}</strong></span>
                    <span>Viewed: <strong>{question.views}</strong></span>
                </div>
            </div>

            {/* Question and Answers Container */}
            <div className="row">
                <div className="col-12">
                    {/* Question Content */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                {/* Voting Column */}
                                <div className="col-1 text-center">
                                    <button className="btn btn-link p-0">
                                        <ArrowUp size={20} className="text-secondary" />
                                    </button>
                                    <div className="fs-5 my-2">{question.votes}</div>
                                    <button className="btn btn-link p-0">
                                        <ArrowDown size={20} className="text-secondary" />
                                    </button>
                                </div>

                                {/* Main Content Column */}
                                <div className="col-9">
                                    <div className="question-content mb-4">
                                        {question.content}
                                    </div>

                                    {/* Tags */}
                                    <div className="mb-3">
                                        {question.tags.map(tag => (
                                            <span key={tag} className="badge bg-light text-secondary me-2">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>


                                {/* Author Card */}
                                <div className="col-2">
                                    <div className="d-flex justify-content-end">
                                        <div className="bg-light p-3 rounded">
                                            <div className="d-flex gap-3 align-items-center">

                                                <div>
                                                    <div>{question.author}</div>
                                                    <div className="text-secondary small">
                                                        reputation: -
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>

                    {/* Answers Section */}
                    <div className="answers-section">
                        <h3 className="mb-4">{answers.length} {t("QuestionDetail.answers")}</h3>

                        {answers.map(answer => (
                            <div key={answer.answer_id} className="card mb-4">
                                <div className="card-body">
                                    <div className="row">
                                        {/* Answer Voting Column */}
                                        <div className="col-1 text-center">
                                            <button className="btn btn-link p-0">
                                                <ArrowUp size={20} className="text-secondary" />
                                            </button>
                                            <div className="fs-5 my-2">{answer.votes}</div>
                                            <button className="btn btn-link p-0">
                                                <ArrowDown size={20} className="text-secondary" />
                                            </button>
                                            {answer.isAccepted == 1 && (
                                                <div className="mt-3">
                                                    <Award size={20} className="text-success" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Answer Content Column */}
                                        <div className="col-9">
                                            <div className="answer-content mb-4">
                                                {answer.content}
                                            </div>



                                            {/* Comments Section */}
                                            {answer.comments && answer.comments.length > 0 && (
                                                <div className="comments-section mt-4 border-top pt-3">
                                                    {answer.comments.map(comment => (
                                                        <div key={comment.comment_id} className="comment mb-2 text-secondary">
                                                            <small>
                                                                {comment.content} - {comment.author} ({comment.inserted_at})
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}


                                            {/* Add Comment Button TODO */}
                                            <div className="mt-3">

                                                <CommentForm
                                                    parentId={answer.answer_id}
                                                    parentType="answer"
                                                    onSubmit={handleCommentSubmit}
                                                />
                                            </div>

                                        </div>

                                        <div className="col-2">
                                            {/* Answer Author Card */}
                                            <div className="d-flex justify-content-end">
                                                <div className="bg-light p-3 rounded">
                                                    <div className="d-flex gap-3 align-items-center">

                                                        <div>
                                                            <div>{answer.author}</div>
                                                            <div className="text-secondary small">
                                                                reputation: -
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Answer Form */}
                    <div className="card">
                        <div className="card-body">
                            <h3 className="h5 mb-3">{t("QuestionDetail.your_answer")}</h3>
                            <textarea
                                className="form-control mb-3"
                                rows={6}
                                placeholder="Write your answer here..."

                                value={formData.content}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    content: e.target.value
                                }))}

                            ></textarea>
                            <button className="btn btn-primary" onClick={handleSubmitAnswer}>{t("QuestionDetail.post_answer")}</button>
                        </div>
                    </div>
                </div>
            </div>

        </>

    );
};

export default QuestionDetail;